<template>
    <div class="content">
        <radio-buttons_search
            :radio-button-list="selectAreaRadiobuttonSelectListData"
            :list-heading="listHeading"
            :default-radio-button="defaultSelection"
            class="area-radios"
            @selected-radio-button="handleSelectionRadioButton"
            @selected-drop-down="handleSelectionDropDown"
        />
    </div>
</template>

<script lang="ts">
    import { Vue, Component, Prop, Watch, Emit } from "vue-facing-decorator"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import RadioButtonListWithDropdownSearch from "@/components/RadioButtonListWithDropdownSearch.vue"
    import MunicipalitiesAndRegionsModel from "../models/municipalitiesandregionsmodel"
    import DropDownListModel from "@/models/dropdownlistmodel"
    import { AreaTypes } from "@/helpers/enums"

    @Component({
        components: {
            "radio-buttons_search": RadioButtonListWithDropdownSearch
        }
    })
    export default class MunicipalitiesAndProvinces extends Vue {
        /** Otsikko */
        @Prop({ default: "App.RadioButtonListHeader.SelectArea" }) listHeading!: string
        @Prop({ type: Boolean, default: true }) showAllDropDownsDisabled!: boolean
        /** Kunta checkbox on valittuna oletuksena */
        @Prop({ default: AreaTypes.Municipality }) selectedAreaType!: AreaTypes
        /** Näytettävät radiobuttonit (kunta,maakunta,kokomaa) */
        @Prop({ type: Array, default: () => [AreaTypes.Municipality, AreaTypes.Province, AreaTypes.Country] }) visibleAreaTypes!: Array<AreaTypes>

        /** Palautta valitun arvon eli kuntanro tai maakuntanro*/
        @Emit("selectedListValue")
        listValueChange(value) {
            return value
        }

        /** Palauttaa valitun radiobutton valuen: "kunta, maakunta, kokosuomi" */
        @Emit("selectedRadioButton")
        radioValueChange(value) {
            return value
        }

        municipalitiesData: Array<MunicipalitiesAndRegionsModel> = []
        regionsData: Array<MunicipalitiesAndRegionsModel> = []
        municipalities: DropDownListModel[] = []
        regions: DropDownListModel[] = []

        async mounted(): Promise<void> {
            this.municipalitiesData = await (this.$store.getters.municipalitiesData as Array<MunicipalitiesAndRegionsModel>)
            this.regionsData = await (this.$store.getters.regionsData as Array<MunicipalitiesAndRegionsModel>)
            await this.reloadMuncipalitiesAndRegions()
        }

        /** vahditaan jos käyttäjä vaihtaa kieltä */
        @Watch("activeLang")
        /** Haetaan storesta valitun kielin mukaiset kunnat ja maakunnat */
        async reloadMuncipalitiesAndRegions() {
            // Character mapissa skandit on järjestyksessä äåö kun aakkostuksessa åäö. Siirretään å ja Å ennen ä:tä.
            // Huomaa että tämä sort alla järjestää isot ennen pieniä, mutta isot skandit on pienten jälkeen. Ei haittaa tässä mutta
            // jos kopiot muualle niin saattaa olla merkitystä.
            const mapCharacters = (str) => str.replace("å", "ß").replace("Å", "½")

            const sortFunc = (n1, n2) => {
                if (mapCharacters(n1.labelText) > mapCharacters(n2.labelText)) {
                    return 1
                }

                if (mapCharacters(n1.labelText) < mapCharacters(n2.labelText)) {
                    return -1
                }
                return 0
            }

            if (this.visibleAreaTypes.indexOf(AreaTypes.Municipality) > -1) {
                this.municipalities = []
                for (let i = 0; i < this.municipalitiesData.length; i++) {
                    if (this.activeLang !== "sv") {
                        this.municipalities.push(new DropDownListModel(this.municipalitiesData[i].nimi, this.municipalitiesData[i].kuntanumero ?? "", false))
                    }
                    else {
                        this.municipalities.push(new DropDownListModel(this.municipalitiesData[i].namn ?? this.municipalitiesData[i].nimi, this.municipalitiesData[i].kuntanumero ?? "", false))
                    }
                }
                this.municipalities = this.municipalities.sort(sortFunc)
            }
            if (this.visibleAreaTypes.indexOf(AreaTypes.Province) > -1) {
                this.regions = []
                for (let i = 0; i < this.regionsData.length; i++) {
                    if (this.activeLang !== "sv") {
                        this.regions.push(new DropDownListModel(this.regionsData[i].nimi, this.regionsData[i].maakuntanumero, false))
                    }
                    else {
                        this.regions.push(new DropDownListModel(this.regionsData[i].namn ?? this.regionsData[i].nimi, this.regionsData[i].maakuntanumero, false))
                    }
                }
                this.regions = this.regions.sort(sortFunc)
            }
        }

        // Arvot, joilla kunta/maakunta/kokosuomi/ruudut valinta näytetään
        get selectAreaRadiobuttonSelectListData(): Array<RadioButtonModel> {
            const listData: Array<RadioButtonModel> = []
            if (this.visibleAreaTypes.indexOf(AreaTypes.Municipality) > -1)listData.push(new RadioButtonModel("App.RadioButtonList.Municipality", "kunta", "", this.municipalities, this.getSelectedMunicipality()))
            if (this.visibleAreaTypes.indexOf(AreaTypes.Province) > -1) listData.push(new RadioButtonModel("App.RadioButtonList.Province", "maakunta", "", this.regions, this.getSelectedProvince()))
            if (this.visibleAreaTypes.indexOf(AreaTypes.Country) > -1) listData.push(new RadioButtonModel("App.RadioButtonList.EntireFinland", "kokosuomi"))
            return listData
        }

        get defaultSelection(): string {
            if (this.selectAreaRadiobuttonSelectListData) {
                switch (this.selectedAreaType) {
                    case AreaTypes.Municipality:
                        return "App.RadioButtonList.Municipality"
                    case AreaTypes.Province:
                        return "App.RadioButtonList.Province"
                    case AreaTypes.Country:
                        return "App.RadioButtonList.EntireFinland"
                    default:
                        return "App.RadioButtonList.Municipality"
                }
            }
            return "App.RadioButtonList.Municipality"
        }

        get activeLang(): string {
            return this.$store.getters.activeLang
        }

        // Palautetaan radio value eli Kunta, maakunta tai kokosuomi
        handleSelectionRadioButton(item: RadioButtonModel) {
            this.radioValueChange(item.radioValue)
        }

        // Palautetaan joko kuntanro tai maakuntanro
        handleSelectionDropDown(item: string) {
            this.listValueChange(item)
        }

        getSelectedMunicipality(): string {
            return this.$store.getters.selectedMunicipality
        }

        getSelectedProvince(): string {
            return this.$store.getters.selectedProvince
        }
    }
</script>

<style scoped>

    .content {
        margin-top: 2rem !important;
    }

    .area-radios {
        margin-bottom: 20px;
    }

</style>
